/**
 * utils/directoryScanner.js
 * Utilitaire pour scanner les répertoires et trouver les fichiers .sor
 */

export class DirectoryScanner {
  /**
   * Scanner un répertoire et ses sous-répertoires pour trouver les fichiers .sor
   * @param {FileSystemDirectoryHandle} directoryHandle - Handle du répertoire sélectionné
   * @param {Object} [options] - Options de scan
   * @param {boolean} [options.allowEmpty=false] - Si true, accepte les dossiers vides
   * @returns {Promise<Object>} Résultat du scan avec les fichiers trouvés
   */
  static async scanDirectory(directoryHandle, options = {}) {
    const { allowEmpty = false } = options;

    try {
      const files = [];
      await this._scanRecursive(directoryHandle, files, "");

      if (files.length === 0) {
        if (allowEmpty) {
          // Accepter les dossiers vides si allowEmpty est activé
          return {
            isValid: true,
            data: { files: [] },
            errors: [],
          };
        }

        return {
          isValid: false,
          errors: ["Aucun fichier .sor trouvé dans le répertoire sélectionné"],
          data: null,
        };
      }

      return {
        isValid: true,
        data: { files },
        errors: [],
      };
    } catch (error) {
      return {
        isValid: false,
        errors: [`Erreur lors du scan: ${error.message}`],
        data: null,
      };
    }
  }

  /**
   * Scanner récursivement un répertoire
   */
  static async _scanRecursive(directoryHandle, files, relativePath) {
    for await (const entry of directoryHandle.values()) {
      const currentPath = relativePath ? `${relativePath}/${entry.name}` : entry.name;

      if (entry.kind === "file" && entry.name.toLowerCase().endsWith(".sor")) {
        const file = await entry.getFile();
        files.push({
          name: entry.name,
          path: currentPath,
          size: file.size,
          file: file,
        });
      } else if (entry.kind === "directory") {
        await this._scanRecursive(entry, files, currentPath);
      }
    }
  }

  /**
   * Vérifier si le navigateur supporte l'API File System Access
   */
  static isSupported() {
    return "showDirectoryPicker" in window;
  }
}
